// apps/web/src/features/cargo/components/cargoColumns.tsx
//
// Column model for the Cargo registry table (#1231). Source columns retain
// their order; display-only density (#2581) sits directly after volume. The
// index + article columns are sticky-left. Money/quantity and backend-derived
// values render as provided; only density is computed client-side.

import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import {
  CARGO_CARRIER_PAYMENT_STATUS_LABELS,
  CARGO_CLIENT_PAYMENT_STATUS_LABELS,
  type CargoArticleWire,
} from '@magimp/shared';

import { Icon } from '../../../ui/index.js';
import {
  CargoClientPaymentCell,
  CargoClientTariffCell,
  CargoDeleteCell,
  type CargoRowActions,
} from './cargoActionCells.js';
import { CargoStatusPill } from './CargoStatusPill.js';
import { formatCargoDensity } from '../numeric.js';

// Narrowed to the `cargo` namespace so cell helpers can call the
// `cargo:`-prefixed keys the locale files define.
export type CargoT = TFunction<readonly ['cargo']>;

// Column ids. `index` is the `#` gutter (no i18n header); every other id has a
// matching `cargo:columns.<id>` entry in all three locales.
export type CargoColumnKey =
  | 'index'
  | 'article'
  | 'totalCost'
  | 'daysWithoutPayment'
  | 'manager'
  | 'carrier'
  | 'pathFormat'
  | 'status'
  | 'nameGoods'
  | 'numberOfSeats'
  | 'weight'
  | 'volume'
  | 'density'
  | 'transportationTariff'
  | 'transportationTariffForClients'
  | 'costGoods'
  | 'insuranceCost'
  | 'packagingCost'
  | 'timeFromChina'
  | 'chinaPayment'
  | 'chinaWarehouseDispatch'
  | 'chinaWarehouseInspection'
  | 'chinaWarehousePackaging'
  | 'chinaWarehouseTotal'
  | 'addressTransportationCost'
  | 'prr'
  | 'tatCost'
  | 'clientPaymentStatus'
  | 'carrierPaymentStatus'
  | 'timeCargoArrivalToRf'
  | 'timeCargoRelease'
  | 'daysOnTheWay'
  | 'delete';

/** Typed `cargo:columns.*` header key for a non-gutter column. */
export function cargoColumnHeaderKey(
  key: Exclude<CargoColumnKey, 'index'>,
): `cargo:columns.${Exclude<CargoColumnKey, 'index'>}` {
  return `cargo:columns.${key}`;
}

export interface CargoColumn {
  /** Stable id; also the i18n key suffix under `cargo:columns.*`. */
  key: CargoColumnKey;
  /** Sticky-left (the `#` index and the article). */
  sticky?: boolean;
  /** Admin / logist_ru / logist_cn only (#24, #28). */
  logisticOnly?: boolean;
  /** Numeric / date cells right-align; text cells start-align. */
  align?: 'start' | 'end' | 'center';
  /** Fixed rendered column width in px (border-box). Drives a dense, content-sized grid. */
  width: number;
  render: (row: CargoArticleWire, ctx: CargoCellContext, index: number) => ReactNode;
}

export interface CargoCellContext {
  t: CargoT;
  /** `Intl` locale tag for date formatting. */
  locale: string;
  /**
   * Interactive row actions (#1232). When present, the status / client-tariff /
   * client-payment / delete cells render their interactive controls; when
   * absent (e.g. the #1231 read-only registry or unauthorized actors) the cells
   * fall back to plain display.
   */
  actions?: CargoRowActions;
}

const EMPTY = '—';

function text(value: string | null | undefined): ReactNode {
  return value && value.length > 0 ? value : EMPTY;
}

/** Format a `\d+\.\d{2}` money string with a leading ¥ symbol, empty when null. */
function yuan(value: string | null | undefined): ReactNode {
  if (!value) return EMPTY;
  return `¥${value}`;
}

function num(value: number | null | undefined): ReactNode {
  return value == null ? EMPTY : String(value);
}

function dateOnly(iso: string | null, locale: string): ReactNode {
  if (!iso) return EMPTY;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

/**
 * @aiPurpose ordered column model. The page filters out
 *   `logisticOnly` columns for non-logistic actors before rendering.
 */
export const CARGO_COLUMNS: readonly CargoColumn[] = [
  {
    key: 'index',
    sticky: true,
    align: 'end',
    // must equal INDEX_COL_WIDTH in CargoRegistryTable.tsx (folds #1903 seam)
    width: 48,
    render: (_r, _c, index) => index,
  },
  { key: 'article', sticky: true, width: 124, render: (r) => text(r.article) },
  { key: 'totalCost', align: 'end', width: 72, render: (r) => text(r.totalCost) },
  { key: 'daysWithoutPayment', align: 'end', width: 54, render: (r) => num(r.daysWithoutPayment) },
  {
    key: 'manager',
    width: 84,
    render: (r, c) =>
      text(r.responsibleManager ? r.responsibleManager.name : c.t('cargo:cells.noManager')),
  },
  { key: 'carrier', width: 80, render: (r) => text(r.carrier) },
  { key: 'pathFormat', width: 76, render: (r) => text(r.pathFormat) },
  {
    key: 'status',
    width: 88,
    render: (r) => <CargoStatusPill row={r} />,
  },
  { key: 'nameGoods', width: 112, render: (r) => text(r.nameGoods) },
  { key: 'numberOfSeats', align: 'end', width: 54, render: (r) => text(r.numberOfSeats) },
  { key: 'weight', align: 'end', width: 54, render: (r) => text(r.weight) },
  { key: 'volume', align: 'end', width: 56, render: (r) => text(r.volume) },
  {
    key: 'density',
    align: 'end',
    width: 72,
    render: (r) => text(formatCargoDensity(r.weight, r.volume)),
  },
  {
    key: 'transportationTariff',
    align: 'end',
    width: 60,
    render: (r) => text(r.transportationTariff),
  },
  {
    key: 'transportationTariffForClients',
    align: 'end',
    width: 72,
    render: (r, c) =>
      c.actions ? (
        <CargoClientTariffCell row={r} actions={c.actions} />
      ) : (
        text(r.transportationTariffForClients)
      ),
  },
  { key: 'costGoods', align: 'end', width: 66, render: (r) => text(r.costGoods) },
  { key: 'insuranceCost', align: 'end', width: 66, render: (r) => text(r.insuranceCost) },
  { key: 'packagingCost', align: 'end', width: 66, render: (r) => text(r.packagingCost) },
  {
    key: 'timeFromChina',
    align: 'end',
    width: 72,
    render: (r, c) => dateOnly(r.timeFromChina, c.locale),
  },
  { key: 'chinaPayment', align: 'end', width: 68, render: (r) => yuan(r.chinaPaymentAmount) },
  {
    key: 'chinaWarehouseDispatch',
    align: 'end',
    width: 68,
    render: (r) => yuan(r.chinaWarehouseDispatchAmount),
  },
  {
    key: 'chinaWarehouseInspection',
    align: 'end',
    width: 68,
    render: (r) => yuan(r.chinaWarehouseInspectionAmount),
  },
  {
    key: 'chinaWarehousePackaging',
    align: 'end',
    width: 68,
    render: (r) => yuan(r.chinaWarehousePackagingAmount),
  },
  {
    key: 'chinaWarehouseTotal',
    align: 'end',
    width: 64,
    render: (r) => yuan(r.chinaWarehouseTotalAmount),
  },
  {
    key: 'addressTransportationCost',
    align: 'end',
    width: 70,
    render: (r) => text(r.addressTransportationCost),
  },
  { key: 'prr', align: 'end', width: 48, render: (r) => text(r.prr) },
  { key: 'tatCost', align: 'end', width: 60, render: (r) => text(r.tatCost) },
  {
    key: 'clientPaymentStatus',
    width: 180,
    render: (r, c) =>
      c.actions ? (
        <CargoClientPaymentCell row={r} t={c.t} actions={c.actions} />
      ) : (
        CARGO_CLIENT_PAYMENT_STATUS_LABELS[r.paidByClientStatus]
      ),
  },
  {
    key: 'carrierPaymentStatus',
    logisticOnly: true,
    width: 92,
    render: (r) => CARGO_CARRIER_PAYMENT_STATUS_LABELS[r.paymentToCarrierStatus],
  },
  {
    key: 'timeCargoArrivalToRf',
    align: 'end',
    width: 72,
    render: (r, c) => dateOnly(r.timeCargoArrivalToRf, c.locale),
  },
  {
    key: 'timeCargoRelease',
    align: 'end',
    width: 72,
    render: (r, c) => dateOnly(r.timeCargoRelease, c.locale),
  },
  { key: 'daysOnTheWay', align: 'end', width: 56, render: (r) => num(r.daysOnTheWay) },
  {
    key: 'delete',
    logisticOnly: true,
    align: 'center',
    width: 42,
    render: (r, c) =>
      c.actions ? (
        <CargoDeleteCell row={r} t={c.t} actions={c.actions} />
      ) : (
        <span
          aria-label={c.t('cargo:cells.deleteDisabled')}
          title={c.t('cargo:cells.deleteDisabled')}
        >
          <Icon name="trash-2" size={14} />
        </span>
      ),
  },
];

/** Columns visible for the given actor cohort, preserving source order. */
export function visibleCargoColumns(canSeeLogisticColumns: boolean): readonly CargoColumn[] {
  if (canSeeLogisticColumns) return CARGO_COLUMNS;
  return CARGO_COLUMNS.filter((c) => !c.logisticOnly);
}
